import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

export default function PrintLabel() {
  const API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();

  // State
  const [awbInput, setAwbInput] = useState("");
  const [awbList, setAwbList] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Read AWBs from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const awbsParam = params.get("awbs");
    if (awbsParam) {
      const awbs = awbsParam.split(",").map(a => a.trim()).filter(a => a);
      if (awbs.length > 0) {
        setAwbList(awbs);
        setSuccess(`Loaded ${awbs.length} AWB(s) from bulk order`);
        setTimeout(() => setSuccess(""), 3000);
      }
    }
  }, [location.search]);

  // Extract and format AWB numbers from text
  const extractAwbNumbers = (text) => {
    const parts = text
      .split(/[\s,;\n\r\t]+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);

    const validAwbs = parts.filter(p => /^[A-Za-z]{2,4}.*\d+/i.test(p));
    return [...new Set(validAwbs)];
  };

  // Handle paste - extract and format in textarea
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const extracted = extractAwbNumbers(pastedText);
    
    if (extracted.length > 0) {
      setAwbInput(extracted.join("\n"));
    } else {
      setAwbInput(prev => prev + pastedText);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setAwbInput(e.target.value);
  };

  // Add AWBs from textarea to list
  const addAwbsToList = () => {
    const extracted = extractAwbNumbers(awbInput);
    
    if (extracted.length === 0) {
      setError("No valid AWB numbers found");
      setTimeout(() => setError(""), 2000);
      return;
    }

    setAwbList(prev => {
      const newList = [...prev];
      let addedCount = 0;
      extracted.forEach(awb => {
        if (!newList.includes(awb)) {
          newList.push(awb);
          addedCount++;
        }
      });
      if (addedCount > 0) {
        setSuccess(`Added ${addedCount} AWB(s)`);
        setTimeout(() => setSuccess(""), 2000);
      }
      return newList;
    });
    
    setAwbInput("");
    setError("");
  };

  // Remove AWB from list
  const removeAwb = (awb) => {
    setAwbList(prev => prev.filter(a => a !== awb));
  };

  // Clear all
  const clearAll = () => {
    setAwbList([]);
    setPdfUrl(null);
    setError("");
    setSuccess("");
    setAwbInput("");
  };

  // Generate PDF for single AWB
  const generateSinglePdf = async (awb) => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/labels/generate-pdf?bkid=${awb}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error("Error generating PDF:", err);
      setError(`Failed to generate label for ${awb}`);
      setPdfUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF for multiple AWBs
  const generateAllPdfs = async () => {
    if (awbList.length === 0) {
      setError("No AWB numbers to generate");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const bkids = awbList.join(",");
      const response = await axios.get(
        `${API_URL}shypvdriverapp/labels/generate-pdf?bkid=${bkids}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setSuccess(`Generated labels for ${awbList.length} AWB(s)`);
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      console.error("Error generating PDFs:", err);
      setError("Failed to generate labels. Please try again.");
      setPdfUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // Download current PDF
  const downloadPdf = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `labels_${new Date().toISOString().split("T")[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Styles
  const styles = {
    container: {
      padding: "32px",
      backgroundColor: "#f8f9fb",
      minHeight: "100vh",
    },
    pageTitle: {
      fontSize: "24px",
      fontWeight: "700",
      color: "#1a1a2e",
      marginBottom: "8px",
    },
    pageSubtitle: {
      fontSize: "14px",
      color: "#6b7280",
      marginBottom: "32px",
    },
    mainContent: {
      display: "grid",
      gridTemplateColumns: "420px 1fr",
      gap: "32px",
      alignItems: "start",
    },
    formSection: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      padding: "28px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      border: "1px solid #eef0f3",
    },
    fieldGroup: {
      marginBottom: "24px",
    },
    fieldLabel: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#1a1a2e",
      marginBottom: "4px",
      display: "block",
    },
    fieldHint: {
      fontSize: "13px",
      color: "#9ca3af",
      marginBottom: "10px",
      display: "block",
    },
    textarea: {
      width: "100%",
      padding: "14px 16px",
      fontSize: "14px",
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      backgroundColor: "#fafbfc",
      transition: "all 0.2s",
      outline: "none",
      resize: "vertical",
      minHeight: "140px",
      fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
      lineHeight: "1.7",
      color: "#374151",
    },
    btnPrimary: {
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      backgroundColor: "#1e3a5f",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.2s",
      width: "100%",
    },
    btnSecondary: {
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: "500",
      color: "#6b7280",
      backgroundColor: "#f3f4f6",
      border: "1px solid #e5e7eb",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    btnSuccess: {
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      backgroundColor: "#059669",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      transition: "all 0.2s",
      width: "100%",
    },
    btnSmall: {
      padding: "8px 16px",
      fontSize: "13px",
      width: "auto",
    },
    divider: {
      height: "1px",
      backgroundColor: "#eef0f3",
      margin: "24px 0",
    },
    listSection: {
      backgroundColor: "#fafbfc",
      borderRadius: "10px",
      padding: "16px",
      border: "1px solid #eef0f3",
    },
    listHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    listTitle: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#374151",
    },
    listCount: {
      fontSize: "12px",
      fontWeight: "500",
      color: "#6b7280",
      backgroundColor: "#e5e7eb",
      padding: "4px 10px",
      borderRadius: "12px",
    },
    awbList: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      maxHeight: "200px",
      overflowY: "auto",
    },
    awbItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 14px",
      backgroundColor: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: "500",
      color: "#374151",
      fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace",
      cursor: "pointer",
      transition: "all 0.15s",
    },
    removeBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "22px",
      height: "22px",
      backgroundColor: "transparent",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      fontSize: "16px",
      color: "#9ca3af",
      fontWeight: "400",
      transition: "all 0.15s",
    },
    alert: {
      padding: "12px 16px",
      borderRadius: "10px",
      marginBottom: "20px",
      fontSize: "14px",
      fontWeight: "500",
    },
    alertError: {
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
    },
    alertSuccess: {
      backgroundColor: "#f0fdf4",
      color: "#059669",
      border: "1px solid #bbf7d0",
    },
    pdfSection: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      border: "1px solid #eef0f3",
      overflow: "hidden",
    },
    pdfHeader: {
      padding: "20px 24px",
      borderBottom: "1px solid #eef0f3",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#fafbfc",
    },
    pdfTitle: {
      fontSize: "15px",
      fontWeight: "600",
      color: "#374151",
    },
    pdfContainer: {
      width: "100%",
      height: "700px",
      backgroundColor: "#f9fafb",
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "400px",
      color: "#9ca3af",
      backgroundColor: "#fafbfc",
    },
    emptyIcon: {
      width: "64px",
      height: "64px",
      backgroundColor: "#f3f4f6",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "16px",
    },
    emptyText: {
      fontSize: "14px",
      color: "#6b7280",
      textAlign: "center",
    },
    buttonRow: {
      display: "flex",
      gap: "12px",
    },
  };

  return (
    <div style={styles.container}>
      {/* Page Header */}
      <h1 style={styles.pageTitle}>Print Labels</h1>
      <p style={styles.pageSubtitle}>
        Generate and download shipping labels for your orders
      </p>

      {/* Alerts */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>{error}</div>
      )}
      {success && (
        <div style={{ ...styles.alert, ...styles.alertSuccess }}>{success}</div>
      )}

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Left Panel - Form */}
        <div style={styles.formSection}>
          {/* AWB Input Field */}
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>AWB Numbers</label>
            <span style={styles.fieldHint}>
              Paste or type AWB numbers. They will be auto-formatted.
            </span>
            <textarea
              style={styles.textarea}
              value={awbInput}
              onChange={handleInputChange}
              onPaste={handlePaste}
              placeholder="Enter AWB numbers here...

UB32-26C0454_1
UB32-26C0455_2"
              onFocus={(e) => {
                e.target.style.borderColor = "#1e3a5f";
                e.target.style.backgroundColor = "#fff";
                e.target.style.boxShadow = "0 0 0 3px rgba(30, 58, 95, 0.08)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.backgroundColor = "#fafbfc";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Add Button */}
          <button
            style={{
              ...styles.btnPrimary,
              opacity: !awbInput.trim() ? 0.5 : 1,
              cursor: !awbInput.trim() ? "not-allowed" : "pointer",
            }}
            onClick={addAwbsToList}
            disabled={!awbInput.trim()}
            onMouseEnter={(e) => {
              if (awbInput.trim()) e.target.style.backgroundColor = "#2c4a6e";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#1e3a5f";
            }}
          >
            Add to List
          </button>

          {/* Divider */}
          {awbList.length > 0 && <div style={styles.divider} />}

          {/* AWB List */}
          {awbList.length > 0 && (
            <>
              <div style={styles.listSection}>
                <div style={styles.listHeader}>
                  <span style={styles.listTitle}>Added AWBs</span>
                  <span style={styles.listCount}>{awbList.length}</span>
                </div>
                <div style={styles.awbList}>
                  {awbList.map((awb, index) => (
                    <div
                      key={index}
                      style={styles.awbItem}
                      onClick={() => generateSinglePdf(awb)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f0f9ff";
                        e.currentTarget.style.borderColor = "#bae6fd";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#ffffff";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                      }}
                    >
                      <span>{awb}</span>
                      <button
                        style={styles.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAwb(awb);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#fee2e2";
                          e.target.style.color = "#dc2626";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          e.target.style.color = "#9ca3af";
                        }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: "16px" }}>
                <button
                  style={{
                    ...styles.btnSuccess,
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                    marginBottom: "12px",
                  }}
                  onClick={generateAllPdfs}
                  disabled={loading}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.backgroundColor = "#047857";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#059669";
                  }}
                >
                  {loading ? "Generating..." : `View All Labels (${awbList.length})`}
                </button>
                <button
                  style={{ ...styles.btnSecondary, width: "100%" }}
                  onClick={clearAll}
                >
                  Clear All
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right Panel - PDF Viewer */}
        <div style={styles.pdfSection}>
          <div style={styles.pdfHeader}>
            <span style={styles.pdfTitle}>Label Preview</span>
            {pdfUrl && (
              <button
                style={{ ...styles.btnPrimary, ...styles.btnSmall }}
                onClick={downloadPdf}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#2c4a6e";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#1e3a5f";
                }}
              >
                Download PDF
              </button>
            )}
          </div>
          <div style={styles.pdfContainer}>
            {loading ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <span style={{ fontSize: "24px", color: "#9ca3af" }}>...</span>
                </div>
                <p style={styles.emptyText}>Generating labels...</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Label Preview"
              />
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>
                  <i className="fa fa-file-pdf-o" style={{ fontSize: "24px", color: "#9ca3af" }} />
                </div>
                <p style={styles.emptyText}>
                  {awbList.length === 0
                    ? "Add AWB numbers to generate labels"
                    : "Click an AWB to preview or View All"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
